/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { Observable, combineLatest, of, BehaviorSubject, Subject } from 'rxjs';
import { switchMap, map, last, distinctUntilChanged } from 'rxjs/operators';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { IMemberAspectItem } from './interfaces/IMemberAspectItem';
import { IMemberAspect } from './interfaces/IMemberAspect';
import { AppState } from 'src/app/services/app-state.service';
import { FbdbRequests } from 'src/app/services/fbdb-requests.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { IMemberAspectItemButReality, MemberAspectItemBase } from './member-aspect-item.base';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import firebase from 'firebase/compat/app';

@Injectable()
export class EnterpriseDashboardService {
	public biEmbeds$: Observable<IMemberAspect>;
	public biIndependentEmbeds$: Observable<IMemberAspect>;
	public enableDashboard$: Observable<boolean>;
	public enableProjectDashboard$: Observable<boolean>;
	public activeDashboard$: BehaviorSubject<IMemberAspectItemButReality>;
	public basicUserEnabledDashboard$: Subject<boolean>;

	constructor(
		private appState: AppState,
		private fbdbRequests: FbdbRequests,
		private db: AngularFireDatabase,
		private featureFlagManager: FeatureFlagManager
	) {
		this.enableDashboard$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.DASHBOARD);
		this.enableProjectDashboard$ = this.featureFlagManager.getFlag$(
			DynamicFlagTypes.DASHBOARD_PROJECT_LEVEL_ENABLED
		);

		this.activeDashboard$ = new BehaviorSubject<IMemberAspectItemButReality>(null);
		this.biEmbeds$ = this.getMemberAspect();
		this.basicUserEnabledDashboard$ = new Subject();
		//this.biIndependentEmbeds$ = this.getMemberIndependentAspect();
	}

	public getBiEmbedUrl(aspectItem: IMemberAspectItem): Observable<string> {
		if (aspectItem.requireMembership) {
			// enterprise level dashboard
			return this.fbdbRequests
				.biembedUrlRequest({
					biEmbedId: aspectItem.id,
					biEmbedMemberId: aspectItem.memberId,
					embed_domain: window.location.origin,
				})
				.pipe(
					last(),
					map((value) => {
						return value.biEmbedUrl;
					})
				);
		} else {
			// project level dashboard
			return this.appState.user.pipe(
				switchMap((user) => {
					return this.fbdbRequests
						.biIndependentEmbedUrlRequest({
							biEmbedId: aspectItem.id,
							userId: user.$id,
							embed_domain: window.location.origin,
							lookerParams: {
								user_attributes: { project_id: aspectItem.userAttributes.projectId },
							},
						})
						.pipe(
							last(),
							map((value) => {
								return value.biEmbedUrl;
							})
						);
				})
			);
		}
	}

	private getMemberAspect(): Observable<IMemberAspect> {
		return this.appState.userId.pipe(
			switchMap((userId) => {
				const indicesBiEmbedsPath = fancyFirebaseGetters.getIndicesBiEmbedsPath(userId);
				const indicesBiEmbedsRef =
					fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(indicesBiEmbedsPath);

				const biEmbedsPath = fancyFirebaseGetters.getBiEmbedsPath();
				const biEmbedsRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(biEmbedsPath);

				return combineLatest([
					this.db.object(indicesBiEmbedsRef).valueChanges(),
					this.db.object(biEmbedsRef).valueChanges(),
				]);
			}),
			map((arr) => {
				let aspect: IMemberAspect;
				const memberBiEmbeds = arr[0];
				const biEmbeds = arr[1];
				const aspectItems: IMemberAspectItem[] = [];
				if (memberBiEmbeds) {
					Object.getOwnPropertyNames(memberBiEmbeds).forEach((bikey) => {
						const board = memberBiEmbeds[bikey];
						Object.getOwnPropertyNames(board).forEach((memberkey) => {
							if (
								board[memberkey] &&
								biEmbeds &&
								biEmbeds[bikey] &&
								!this.isIndependentAspectItem(biEmbeds[bikey])
							) {
								aspectItems.push({
									id: bikey,
									memberId: memberkey,
									name: biEmbeds[bikey].name,
									displayText: biEmbeds[bikey].name,
									searchText: biEmbeds[bikey].name ? biEmbeds[bikey].name.toLowerCase() : '',
									organization: '',
									requireMembership: true,
									order: biEmbeds[bikey].order,
								});
							}
						});
					});
				}

				if (aspectItems.length > 0) {
					aspect = {
						name: 'Dashboards',
						items: aspectItems,
						selection: 0,
						route: 'member',
						displayText: 'dashboard',
						component: undefined,
						type: 'enterprise',
					};
				}

				return aspect;
			}),
			// get the organization information, each user may see multiple dashboards, even the same type, for different organizations
			switchMap((aspect) => {
				if (aspect) {
					const orgObservables: Array<Observable<any>> = [];
					aspect.items.forEach((item) => {
						const path = fancyFirebaseGetters.getBiEmbedMembersPath(item.id, item.memberId);
						const ref = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path);
						orgObservables.push(this.db.object(ref).valueChanges());
					});
					return combineLatest([of(aspect), ...orgObservables]);
				} else {
					return of([]);
				}
			}),
			map((arr) => {
				let aspect: IMemberAspect;
				arr = arr.filter((item) => item !== null && item !== undefined);
				aspect = arr[0];

				if (aspect) {
					const orgs = [];
					for (let i = arr.length - 1; i > 0; i--) {
						if (arr[i] && !arr[i].deletedAt) {
							aspect.items[i - 1].organization = arr[i]['lookerParams'].user_attributes.organization;
							aspect.items[i - 1].displayText = aspect.items[i - 1].name;
							aspect.items[i - 1].searchText = aspect.items[i - 1].displayText.toLowerCase();
							if (!orgs.includes(aspect.items[i - 1].organization)) {
								orgs.push(aspect.items[i - 1].organization);
							}
						} else {
							aspect.items.splice(i - 1, 1);
						}
					}
					// add organization only if user has dashboards for more than one organization.
					if (orgs.length > 1) {
						aspect.items.forEach((item) => {
							item.displayText = item.organization + ' ' + item.displayText;
							item.searchText = item.displayText.toLowerCase();
						});
					}

					// sort alphabatically
					aspect.items.sort((a, b) => a.displayText.localeCompare(b.displayText));
					// but then sort again <_<
					this.sketchyDashboardSort(aspect.items);
				}

				return aspect && aspect.items.length > 0 ? aspect : null;
			})
		);
	}

	public getMemberIndependentAspect(accessLevel: string): Observable<IMemberAspect> {
		const biEmbedsPath = fancyFirebaseGetters.getBiEmbedsPath();
		const biEmbedsRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(biEmbedsPath);
		return this.db
			.object(biEmbedsRef)
			.valueChanges()
			.pipe(
				map((biEmbeds) => {
					let aspect: IMemberAspect;
					const aspectItems: IMemberAspectItem[] = [];
					if (biEmbeds) {
						Object.getOwnPropertyNames(biEmbeds).forEach((bikey) => {
							// we need a piece of information from backend to indicate any dashboard available to all users
							if (
								this.isIndependentAspectItem(biEmbeds[bikey]) &&
								this.hasNeededAccessLevel(biEmbeds[bikey], accessLevel)
							) {
								aspectItems.push({
									id: bikey,
									name: biEmbeds[bikey].name,
									memberId: undefined,
									displayText: biEmbeds[bikey].name,
									searchText: biEmbeds[bikey].name ? biEmbeds[bikey].name.toLowerCase() : '',
									organization: '',
									requireMembership: false,
									accessLevel: biEmbeds[bikey].accessLevel, // undefined is ok
									order: biEmbeds[bikey].order,
								});
							}
						});

						this.sketchyDashboardSort(aspectItems);
					}

					if (aspectItems.length > 0) {
						aspect = {
							name: 'Dashboards',
							items: aspectItems,
							selection: 0,
							route: 'member',
							displayText: 'dashboard',
							component: undefined,
							type: 'independent',
						};
					}

					return aspect;
				})
			);
	}

	// descending sort - higher numbers first
	// nulls/ undefineds will always sort below, and will be "unsorted".
	// Practically speaking that's either undefined order or id ordered
	private sketchyDashboardSort(items: IMemberAspectItem[]) {
		const undef = (a: unknown) => a === undefined || a === null;

		if (!items?.length) {
			return;
		}

		items.sort((a, b) => {
			if (undef(a.order) && undef(b.order)) {
				return 0;
			} else if (undef(a.order)) {
				return 1;
			} else if (undef(b.order)) {
				return -1;
			} else {
				return b.order - a.order;
			}
		});
	}

	public setActiveDashboard(board: IMemberAspectItemButReality) {
		this.activeDashboard$.next(board);
	}

	/**
	 *  If requiresBiEmbedMembership does not exist on a dashboard definition, it is project dashboard at this time
	 *  If requiresBiEmbedMembership exist on dashboard definition, it needs to be false to be a project level dasnboard
	 */
	private isIndependentAspectItem(biEmbed): boolean {
		return biEmbed.requiresBiEmbedMembership !== undefined && !biEmbed.requiresBiEmbedMembership;
	}

	/**
	 *  If accessLevel does not exist on a project level dashboard definition, it is accessible to everyone
	 *  If accessLevel exist on a project level dashboard definition, the users project access level has to match it to get access
	 */
	private hasNeededAccessLevel(biEmbed, accessLevel): boolean {
		return biEmbed.accessLevel === undefined || biEmbed.accessLevel === accessLevel;
	}
}
